// src/components/pages/DocumentCategory/DocumentCategoryComponent.js
import React, { useLayoutEffect, useState, useEffect, useRef } from "react";
import DataGrid, {
  Column,
  Export,
  HeaderFilter,
  Pager,
  Paging,
  SearchPanel,
} from "devextreme-react/data-grid";
import Modal from "react-modal";
import Swal from "sweetalert2";
import { TextBox } from "devextreme-react";
import { useSelector } from "react-redux";
import { Icon } from "@iconify/react";
import { documentCategoryService } from "../../../apis/apiServices/DocumentCategory";

export default function DocumentCategoryComponent() {
  useLayoutEffect(() => {
    const el = document.querySelector("#pageTitle");
    if (el) el.innerHTML = "Document Category";
  }, []);

  const user = useSelector((state) => state.user?.data);
  const refDataGrid = useRef(null);

  const [list, setList] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [model, setModel] = useState({
    id: 0,
    categoryName: "",
    companyId: 0,
  });

  const normalizeRespToArray = (resp) => {
    const data = resp?.data ?? resp;
    if (Array.isArray(data)) return data;
    if (Array.isArray(resp?.data?.data)) return resp.data.data;
    if (Array.isArray(data?.items)) return data.items;
    return [];
  };

  const getList = async () => {
    try {
      if (refDataGrid?.current?.instance?.beginCustomLoading)
        refDataGrid.current.instance.beginCustomLoading();

      const resp = await documentCategoryService.getDocumentCategoryList();
      //console.log("[DocumentCategory] raw resp:", resp);
      const normalized = normalizeRespToArray(resp);
      setList(normalized || []);
      //console.log("[DocumentCategory] list length:", (normalized || []).length);
    } catch (err) {
      console.error("[DocumentCategory] getList error", err);
      const status = err?.response?.status;
      if (status === 401) {
        Swal.fire("Unauthorized", "Please login.", "warning");
      } else if (status === 403) {
        Swal.fire(
          "Forbidden",
          "You do not have access to view document categories.",
          "error"
        );
      } else {
        Swal.fire("Error", "Failed to fetch document categories.", "error");
      }
    } finally {
      if (refDataGrid?.current?.instance?.endCustomLoading)
        refDataGrid.current.instance.endCustomLoading();
    }
  };

  useEffect(() => {
    getList();
    // eslint-disable-next-line
  }, []);

  const openAddModal = () => {
    setModel({ id: 0, categoryName: "", companyId: 0 });
    setErrors({});
    setIsSubmitted(false);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setIsSubmitted(false);
    setErrors({});
    setModel({ id: 0, categoryName: "", companyId: 0 });
  };

  const handleRowClick = (e) => {
    setModel({
      id: e?.data?.id ?? 0,
      categoryName: e?.data?.category_name ?? e?.data?.categoryName ?? "",
      companyId: e?.data?.company_id ?? e?.data?.companyId ?? 0,
    });
  };

  const handleInputChange = (e, field) => {
    setErrors((prev) => ({ ...prev, [field]: "" }));
    const val = e?.value ?? e?.target?.value;
    setModel((prev) => ({ ...prev, [field]: val }));
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    setIsSubmitted(true);

    const newErrors = {};
    if (!model.categoryName)
      newErrors.categoryName = "Please enter category name";

    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      setIsSubmitted(false);
      return;
    }

    try {
      // force companyId to 0 per request
      const body = {
        id: model.id,
        categoryName: model.categoryName,
        companyId: 0,
        ModifiedBy: user?.userId,
      };

      const resp = await documentCategoryService.addDocumentCategory(body);
      console.log("[DocumentCategory] add/update resp:", resp);

      // handle different success shapes
      const ok =
        resp?.isSuccess ??
        resp?.success ??
        resp?.data?.isSuccess ??
        (resp?.status === 200 || resp?.status === 201);
      const serverMessage = resp?.message ?? resp?.data?.message ?? "Saved";

      if (!ok) {
        Swal.fire("Error!", serverMessage || "Failed", "error");
        setErrors(resp?.errors || {});
        setIsSubmitted(false);
        return;
      }

      // Determine friendly message based on create/update
      const successMessage =
        body.id && body.id > 0
          ? `Document Category "${body.categoryName}" Updated Successfully`
          : `Document Category "${body.categoryName}" Created Successfully`;

      await getList();
      Swal.fire("Success", successMessage, "success").then(() => {
        closeModal();
      });
    } catch (err) {
      console.error("[DocumentCategory] add/update error", err);
      const message =
        err?.response?.data?.message ?? err?.message ?? "Something went wrong";
      Swal.fire("Error!", message, "error");
    } finally {
      setIsSubmitted(false);
    }
  };

  // improved delete handler that tolerates 204 / ApiResponse / plain success
  const handleDelete = async (id) => {
    if (!id) return;
    // show the name in confirmation if available
    const name =
      list.find(
        (item) =>
          (item.id ?? item.Id ?? item.categoryId ?? item.category_id) === id
      )?.category_name ??
      list.find(
        (item) =>
          (item.id ?? item.Id ?? item.categoryId ?? item.category_id) === id
      )?.categoryName ??
      list.find(
        (item) =>
          (item.id ?? item.Id ?? item.categoryId ?? item.category_id) === id
      )?.category ??
      "";

    const confirmText = name
      ? `Are you sure you want to delete "${name}"? This will permanently delete the document category.`
      : "This will permanently delete the document category";

    Swal.fire({
      title: "Are you sure?",
      text: confirmText,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
    }).then(async (result) => {
      if (!result.isConfirmed) return;

      try {
        if (refDataGrid?.current?.instance?.beginCustomLoading)
          refDataGrid.current.instance.beginCustomLoading();

        const resp = await documentCategoryService.deleteDocumentCategory(id);
        console.log("[DocumentCategory] delete resp:", resp);

        const ok =
          resp?.isSuccess === true ||
          resp?.success === true ||
          resp?.status === 204 ||
          resp === true ||
          (resp && typeof resp === "object" && Object.keys(resp).length > 0);

        if (!ok) {
          const message =
            resp?.message ?? resp?.data?.message ?? "Failed to delete";
          Swal.fire("Error!", message, "error");
          return;
        }

        const successMsg = name
          ? `Document Category "${name}" Deleted Successfully`
          : "Document Category Deleted Successfully";

        Swal.fire("Success", successMsg, "success");
        await getList();
      } catch (err) {
        console.error("[DocumentCategory] delete error:", err);
        const message =
          err?.response?.data?.message ??
          err?.message ??
          "Something went wrong";
        Swal.fire("Error!", message, "error");
      } finally {
        if (refDataGrid?.current?.instance?.endCustomLoading)
          refDataGrid.current.instance.endCustomLoading();
      }
    });
  };

  const renderActionCell = (cellData) => {
    return (
      <span>
        <Icon
          className="iconify-size mr-2"
          title="Edit"
          icon="solar:pen-line-duotone"
          style={{ cursor: "pointer" }}
          onClick={() => {
            setModel({
              id: cellData?.data?.id ?? 0,
              categoryName:
                cellData?.data?.category_name ??
                cellData?.data?.categoryName ??
                "",
              companyId:
                cellData?.data?.company_id ?? cellData?.data?.companyId ?? 0,
            });
            setErrors({});
            setIsSubmitted(false);
            setIsModalOpen(true);
          }}
        />
        <Icon
          className="iconify-size"
          title="Delete"
          icon="fluent:delete-32-regular"
          style={{ cursor: "pointer", marginLeft: 8 }}
          onClick={() => handleDelete(cellData.data?.id)}
        />
      </span>
    );
  };

  return (
    <>
      <div className="content">
        <div className="card">
          <div className="card-body">
            <DataGrid
              dataSource={list}
              showColumnLines={false}
              ref={refDataGrid}
              showRowLines
              showBorders
              onRowClick={(e) => handleRowClick(e)}
              allowColumnReordering
              columnAutoWidth
              width={"100%"}
              onToolbarPreparing={(e) => {
                e.toolbarOptions.items.unshift(
                  {
                    location: "before",
                    widget: "dxButton",
                    options: {
                      icon: "plus",
                      text: "Add",
                      type: "default",
                      stylingMode: "contained",
                      onClick: () => openAddModal(),
                    },
                  },
                  {
                    location: "before",
                    widget: "dxButton",
                    options: {
                      icon: "refresh",
                      text: "Refresh",
                      type: "default",
                      stylingMode: "outlined",
                      onClick: () => getList(),
                    },
                  }
                );
              }}
            >
              <HeaderFilter visible={false} />
              <Column width={60} caption={""} cellRender={renderActionCell} />
              <Column dataField="categoryName" caption="Name" />
              <SearchPanel visible placeholder="Search..." width={240} />
              <Paging defaultPageSize={15} />
              <Pager displayMode="compact" showInfo visible />
              <Export enabled={false} fileName="DocumentCategories" />
            </DataGrid>
          </div>
        </div>
      </div>

      {/* Add / Edit Modal */}
      <Modal
        ariaHideApp={false}
        isOpen={isModalOpen}
        className="react-modal resmodel"
        onRequestClose={closeModal}
        shouldCloseOnOverlayClick={false}
      >
        <form
          onSubmit={handleSubmit}
          style={isSubmitted ? { pointerEvents: "none", opacity: 0.4 } : {}}
        >
          <div className="modal-header">
            <div className="modal-title">
              {model.id === 0
                ? "Add Document Category"
                : `Update Document Category [${model.categoryName}]`}
            </div>
          </div>

          <div className="modal-body">
            <div className="row">
              <div className="col-12">
                <div className="form-group">
                  <label>
                    Category Name <span className="text-danger">*</span>
                  </label>
                  <TextBox
                    className="form-control form-control-sm"
                    height={37}
                    placeholder="Enter category name"
                    showClearButton
                    valueChangeEvent="keyup"
                    value={model.categoryName}
                    onValueChanged={(e) => handleInputChange(e, "categoryName")}
                  />
                  {errors.categoryName && (
                    <div className="text-danger">{errors.categoryName}</div>
                  )}
                </div>
              </div>

              {/* Company Id input intentionally removed — companyId will be sent as 0 */}
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={closeModal}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary btn-sm">
              {isSubmitted ? (
                <span>
                  <i className="fas fa-spinner fa-spin"></i> In process
                </span>
              ) : (
                <span>
                  <i className="fas fa-check"></i> Submit
                </span>
              )}
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
}
